import { ChangeDetectionStrategy, Component, computed, forwardRef, input, output } from "@angular/core";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";

import { Subject } from "rxjs";
import { LucideAngularModule, XCircle } from "lucide-angular";

import { NgSelectModule } from "@ng-select/ng-select";
import { CustomIconName } from "@shared/icons/icon-definitions";

import { BaseControlValueAccessorComponent } from "../base/base-control-value-accessor.component";
import { CustomIconComponent } from "../custom-icon/custom-icon.component";

export interface SelectOption<T = any, TData = any> {
  text: string; // Display text for the option
  value: T; // Value of the option
  disabled?: boolean; // Whether the option is disabled
  tooltip?: string; // Optional tooltip
  icon?: string; // Optional icon name
  data?: TData; // Additional data for the option
}

@Component({
  selector: "component-select",
  imports: [FormsModule, LucideAngularModule, NgSelectModule, CustomIconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ],
  templateUrl: "./select.component.html",
  styleUrl: "./select.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectComponent<T = any> extends BaseControlValueAccessorComponent<SelectOption<T> | SelectOption<T>[] | null> {
  readonly options = input<SelectOption<T>[]>([]);
  readonly label = input<string>("");
  readonly placeholderText = input<string>("Select...");
  readonly floating = input<boolean>(false);
  readonly clearable = input<boolean>(false);
  readonly searchable = input<boolean>(false);
  readonly loading = input<boolean>(false);
  readonly typeahead = input<boolean>(false);
  readonly notFoundText = input<string>("No items found");
  readonly bindLabel = input<string>("text");
  readonly bindValue = input<string>("value");
  readonly multiple = input<boolean>(false);
  readonly maxSelectedItems = input<number | null>(null);
  readonly closeOnSelect = input<boolean>(true);
  readonly hideSelected = input<boolean>(false);
  readonly size = input<"sm" | "md" | "lg">("md");
  readonly containerCssClass = input<string>("");
  readonly dropdownCssClass = input<string>("");
  readonly appendTo = input<string>("body");
  readonly showClearButton = input<boolean>(true);
  readonly showPlaceholder = input<boolean>(true);
  readonly secondary = input<boolean>(false);
  readonly dropdownMinWidth = input<string | null>(null);
  readonly errorMessage = input<string | null>(null);
  readonly isFocused = input<boolean>(false);

  /**
   * Custom icon to display as a prefix before the selected value
   */
  readonly prefixIcon = input<CustomIconName | null>(null);
  /**
   * Size of the prefix icon in pixels
   */
  readonly prefixIconSize = input<number>(20);

  /**
   * View box of the prefix icon
   */
  readonly prefixIconViewBox = input<string>("0 0 20 20");

  /**
   * When true, shows only the prefix icon without text or dropdown arrow.
   * Useful for icon-only filter buttons. Requires prefixIcon to be set.
   */
  readonly iconOnly = input<boolean>(false);
  readonly checkboxes = input<boolean>(false);
  readonly customLabel = input<string | null>(null);
  /**
   * When true, adds an "All" option at the beginning of the options list
   * with a null value. Useful for filters where you want to show all items.
   */
  readonly hasAllOption = input<boolean>(false);
  /**
   * Text to display for the "All" option when hasAllOption is true.
   */
  readonly allOptionText = input<string>("All");

  readonly sizeClass = computed(() => {
    switch (this.size()) {
      case "sm":
        return "ng-select-sm";
      case "lg":
        return "ng-select-lg";
      default:
        return "";
    }
  });

  readonly dropdownClass = computed(() => {
    const classes = [this.dropdownCssClass()];
    if (this.iconOnly()) {
      classes.push("icon-only-dropdown");
    }
    return classes.filter(Boolean).join(" ");
  });

  readonly displayOptions = computed(() => {
    const originalOptions = this.options();
    if (!this.hasAllOption()) {
      return originalOptions;
    }

    // Create a copy of the options array with the "All" option at the beginning
    const allOption: SelectOption<T> = {
      text: this.allOptionText(),
      value: null as any // null value to indicate no filter
    };

    return [allOption, ...originalOptions];
  });

  protected readonly hasError = computed(() => !!this.errorMessage());
  protected readonly hasValue = computed(() => this._value() !== null && this._value() !== undefined);
  protected readonly showFloatingLabel = computed(() => {
    return this.floating() && this.label() && (this.isFocused() || this.hasValue() || this.touched);
  });

  protected readonly selectClasses = computed(() => {
    const classes = [this.sizeClass(), this.dropdownClass(), "form-control p-0"];
    if (this.secondary()) {
      classes.push("secondary");
    }
    return classes.filter(Boolean).join(" ");
  });

  readonly singleSelectionChange = output<SelectOption<T> | null>();
  readonly multipleSelectionChange = output<SelectOption<T>[] | null>();
  readonly opened = output<void>();
  readonly closed = output<void>();
  readonly cleared = output<void>();
  readonly search = output<string>();
  readonly add = output<SelectOption<T>>();
  readonly remove = output<SelectOption<T>>();
  readonly scrollToEnd = output<void>();
  readonly focus = output<void>();
  readonly blur = output<void>();

  // For typeahead functionality
  readonly typeaheadInput = new Subject<string>();

  protected readonly xCircleIcon = XCircle;

  protected onItemSelected(value: SelectOption<T> | SelectOption<T>[] | null): void {
    this.onChange(value);
    this.markAsTouched();
    if (this.multiple()) {
      this.multipleSelectionChange.emit(value as SelectOption<T>[]);
    } else {
      this.singleSelectionChange.emit(value as SelectOption<T>);
    }
  }

  protected onClear(): void {
    this.writeValue(null);
    this.onChange(null);
    this.markAsTouched();
    this.cleared.emit();
  }

  protected onOpen(): void {
    this.opened.emit();

    // Apply custom min-width to dropdown panel if specified
    if (this.dropdownMinWidth()) {
      setTimeout(() => {
        const panels = document.querySelectorAll(".ng-dropdown-panel");
        if (panels.length > 0) {
          const lastPanel = panels[panels.length - 1] as HTMLElement;
          lastPanel.style.minWidth = this.dropdownMinWidth()!;
          lastPanel.style.width = this.dropdownMinWidth()!;
        }
      }, 0);
    }
  }

  /**
   * Compare function for ng-select to properly handle object comparisons
   * This is used to determine when two options are equal, even if they're different object references
   */
  protected compareWith(item1: SelectOption<T>, item2: SelectOption<T>): boolean {
    if (!item1 || !item2) {
      return false;
    }

    // First compare by value
    if (typeof item1.value === "object" && typeof item2.value === "object") {
      // For complex objects, check if it's the same object reference
      // Or implement more complex comparison if needed
      return item1.value === item2.value;
    }

    // For primitive values
    return item1.value === item2.value;
  }
}
